import React from 'react'
import clsx from 'clsx'
import NextImage from 'next/image'
import NextLink from 'next/link'

import { Button } from '@wartek-id/button-v1'
import { Text } from '@wartek-id/text-v1'
import { useToast } from '@wartek-id/toast'

import CertificateTooltip from 'app/ProfileRevampPage/components/CertificateTooltip'
import { useProfile } from 'app/ProfileRevampPage/hooks/useProfile'
import { useSETrackEventCertificates } from 'app/ProfileRevampPage/hooks/useSETrackEventCertificates'
import { generateDownloadFile } from 'utils/download'
import BookmarkIcon from 'public/icon/bookmark.svg'

import type { TCertificate } from 'types/sertifikat'

interface ICertificateLinkProps extends React.HTMLAttributes<HTMLDivElement> {
  link: string
  children: React.ReactNode
}
export const CertificateLink = ({
  children,
  link,
  ...props
}: ICertificateLinkProps) => {
  return Boolean(link) ? (
    <div {...props}>
      <NextLink href={link} passHref>
        <a>{children}</a>
      </NextLink>
    </div>
  ) : (
    <div {...props}>{children}</div>
  )
}

interface ICertificateTagProps extends React.HTMLProps<HTMLDivElement> {
  label: string
  icon: string
}
const CertificateTag = ({ label, icon, ...props }: ICertificateTagProps) => {
  const tagIconPath = `/icon/${icon}.svg`
  return (
    <div {...props} className="flex space-x-2 flex-nowrap">
      <div className="flex items-center flex-none w-4 h-4 grow-0">
        <NextImage
          className="object-cover object-center rounded-md"
          src={tagIconPath}
          height="16px"
          width="16px"
        />
      </div>
      <Text
        variant="helper-bold"
        color="subdued"
        className="overflow-wrap-anywhere line-clamp-1"
      >
        {label}
      </Text>
    </div>
  )
}

interface ICertificateCardProps extends React.HTMLAttributes<HTMLDivElement> {
  index: number
  totalRow: number
  data: TCertificate
  showOnboardingTooltip: boolean
}
const CertificateCard = ({
  index,
  totalRow,
  data,
  showOnboardingTooltip,
  ...props
}: ICertificateCardProps) => {
  const [tooltipReferenceElement, setTooltipReferenceElement] =
    React.useState(null)
  const toast = useToast()
  const { isCurrentUser } = useProfile()
  const { id, trainingId, url, name, subject, provider, date } = data
  const certificateDetailURL = React.useMemo(
    () =>
      isCurrentUser
        ? `/sertifikat/${encodeURIComponent(trainingId)}/${encodeURIComponent(
            id
          )}`
        : null,
    [id, isCurrentUser, trainingId]
  )
  const hasDownloadURL = React.useMemo(
    () => isCurrentUser && Boolean(url),
    [isCurrentUser, url]
  )
  const { SETrackCertificateList } = useSETrackEventCertificates()

  const downloadFileHandler = React.useCallback(
    (e) => {
      e.preventDefault()

      SETrackCertificateList('CLICK_DOWNLOAD_CERTIFICATE', {
        index: [index + 1, 'int'],
        training_id: trainingId,
        training_title: name,
        certificate_id: id,
        num_components: totalRow,
      })

      const fileName = url.split('/').pop()
      generateDownloadFile(url, fileName)

      toast({
        message: 'Sedang mengunduh sertifikat...',
      })

      SETrackCertificateList('DOWNLOAD_CERTIFICATE_START', {
        index: [index + 1, 'int'],
        training_id: trainingId,
        training_title: name,
        certificate_id: id,
        num_components: totalRow,
      })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [toast, url]
  )

  return (
    <div className="relative">
      <CertificateLink
        {...props}
        data-testid="profile-certificate-link"
        link={certificateDetailURL}
        onClick={() => {
          SETrackCertificateList('CLICK_CERTIFICATE_CARD', {
            index: [index + 1, 'int'],
            training_id: trainingId,
            training_title: name,
            certificate_id: id,
            num_components: totalRow,
          })
        }}
      >
        <div
          className={clsx(
            "relative rounded-lg shadow-card p-4 bg-surface-default before:left-0 before:top-0 before:content-[''] before:w-1 before:absolute before:h-full before:bg-surface-neutral-subdued before:rounded-l-lg",
            certificateDetailURL && 'hover:bg-[#EFF0F0] active:bg-[#D9DBDD]'
          )}
        >
          <div className="!absolute !top-0 !right-0">
            <BookmarkIcon className="text-surface-neutral-subdued" />
          </div>

          <Text variant="action" className="mb-1 mr-8 line-clamp-2">
            {name}
          </Text>

          <Text color="subdued" className="mb-4 line-clamp-1">
            {subject}
          </Text>

          <div
            className={clsx(
              'flex items-end justify-between w-full space-x-2',
              hasDownloadURL && 'pr-9'
            )}
          >
            <div className="space-y-2">
              <CertificateTag label={provider} icon="manage_accounts" />

              <div ref={setTooltipReferenceElement}>
                <CertificateTag label={date} icon="event" />
              </div>
            </div>
          </div>
        </div>
      </CertificateLink>

      {hasDownloadURL && (
        <div className="absolute right-4 bottom-4">
          <Button
            data-testid="profile-certificate-list-download"
            className="!outline-[#808489]"
            color="white"
            iconOnly
            onClick={downloadFileHandler}
            size="sm"
            variant="solid"
          >
            <NextImage
              src="/icon/file_download.svg"
              height="20px"
              width="20px"
            />
          </Button>
        </div>
      )}

      {showOnboardingTooltip && (
        <CertificateTooltip referenceElement={tooltipReferenceElement} />
      )}
    </div>
  )
}

export default CertificateCard
